/**
 * Layout Variants for Address Page V2
 * 
 * Extracts the different layout combinations based on experiment states.
 * This keeps the main desktop-layout.tsx clean and readable.
 */

import { TwoColumnLayout } from 'components/atomic/templates/two-column-layout';
import { ExperimentalSection } from 'ui/experimental-section';
import { AllowedExperimentKey } from '@tenants/types';
import { LegacyContentWrapper } from '../../legacy/legacy-content-wrapper';
import { SidebarSection } from '../sections/sidebar';
import type { AddressPageCommonData, AddressPageEventHandlers } from '../../shared';
import styles from './layout-variants.module.scss';

const STICKY_NAV_HEIGHT = 61; // 60px + 1px border

export interface LayoutVariantProps {
  mainContent: React.ReactNode;
  heroGallery: React.ReactNode;
  data: AddressPageCommonData;
  handlers: AddressPageEventHandlers;
}

/**
 * Legacy Hero Layout
 * 
 * Gallery is full-width, content uses overlapping sidebar (position: absolute, top: -7em)
 * Adjusts sidebar sticky position based on STICKY_HEADER_TABS experiment
 */
export function LegacyHeroLayout({ mainContent, heroGallery, data, handlers }: LayoutVariantProps) {
  return (
    <>
      {/* Gallery: Full-width (no max-width container) */}
      {heroGallery}

      {/* Content: Overlapping sidebar with STICKY_HEADER_TABS awareness */}
      <ExperimentalSection
        experimentKey={AllowedExperimentKey.STICKY_HEADER_TABS}
        control={
          // No sticky nav: sidebar at top: 100px
          <LegacyContentWrapper
            data={data}
            handlers={handlers}
            mainContent={mainContent}
            sidebarStickyTopOffset={0}
          />
        }
        variation={
          // Sticky nav present: sidebar at top: 161px (100px + 61px)
          <LegacyContentWrapper
            data={data}
            handlers={handlers}
            mainContent={mainContent}
            sidebarStickyTopOffset={STICKY_NAV_HEIGHT}
          />
        }
      />
    </>
  );
}

/**
 * New Hero Layout (V2)
 * 
 * Gallery AND content wrapped in max-width container (1400px)
 * Uses proper two-column layout without overlapping sidebar
 * Sidebar starts below the hero gallery (not overlapping)
 */
export function NewHeroLayout({ mainContent, heroGallery, data, handlers }: LayoutVariantProps) {
  return (
    <section className={styles.newHeroContainer}>
      {/* Gallery: Max-width container (1400px) */}
      {heroGallery}

      {/* Content: Two-column layout */}
      <TwoColumnLayout
        mainContent={mainContent}
        sidebar={<SidebarSection data={data} handlers={handlers} />}
        stickySidebar
      />
    </section>
  );
}
